import { useRef } from "react";
import { Form, Formik, type FormikProps } from "formik";
import * as Yup from "yup";

import { ThemedDropdownField } from "@vega-core/components/Form/Controls/Dropdown/Dropdown/DropdownField";
import { TextareaField } from "@vega-core/components/Form/Fields/TextareaField";
import { ActionaryModal } from "@vega-core/components/Modals/ActionaryModal/ActionaryModal";
import { type OptionType } from "@vega-core/models/options";
import { m } from "@vega-core/paraglide/messages";

export type DeclineStage = "post-allocation" | "post-nda" | "pre-nda";

type DeclineFormValues = {
  reason: string;
  comment?: string;
};

type RejectDealModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (values: { reason: string; comment?: string }) => void;
  isSubmitting?: boolean;
  declineStage: DeclineStage;
};

const labelsToOptions = (labels: string[]): OptionType[] =>
  labels.map((label) => ({ value: label, label }));

const DECLINE_REASONS: Record<DeclineStage, OptionType[]> = {
  "pre-nda": labelsToOptions([
    m.coInvestments_declineDeal_reasons_notInterestedSector(),
    m.coInvestments_declineDeal_reasons_notInterestedGeography(),
    m.coInvestments_declineDeal_reasons_timingNotRight(),
    m.coInvestments_declineDeal_reasons_notReviewingCoInvestments(),
    m.coInvestments_declineDeal_reasons_allocationCapacityConstraints(),
    m.coInvestments_declineDeal_reasons_other(),
  ]),
  "post-nda": labelsToOptions([
    m.coInvestments_declineDeal_reasons_dealStructureNotSuitable(),
    m.coInvestments_declineDeal_reasons_valuationPricingConcerns(),
    m.coInvestments_declineDeal_reasons_portfolioFitIssues(),
    m.coInvestments_declineDeal_reasons_alreadyCommittedCapital(),
    m.coInvestments_declineDeal_reasons_insufficientInformation(),
    m.coInvestments_declineDeal_reasons_investmentCommitteeDeclined(),
    m.coInvestments_declineDeal_reasons_other(),
  ]),
  "post-allocation": labelsToOptions([
    m.coInvestments_declineDeal_reasons_allocationAmountTooSmall(),
    m.coInvestments_declineDeal_reasons_allocationAmountTooLarge(),
    m.coInvestments_declineDeal_reasons_termsNotAcceptable(),
    m.coInvestments_declineDeal_reasons_economicsNotSuitable(),
    m.coInvestments_declineDeal_reasons_investmentCommitteeDeclined(),
    m.coInvestments_declineDeal_reasons_other(),
  ]),
};

const validationSchema = Yup.object({
  reason: Yup.string().required(
    m.coInvestments_declineDeal_validation_reasonRequired(),
  ),
});

export const RejectDealModal = ({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting = false,
  declineStage,
}: RejectDealModalProps) => {
  const formRef = useRef<FormikProps<DeclineFormValues>>(null);

  const handleSubmit = ({ reason, comment }: DeclineFormValues) => {
    onSubmit({ reason, comment });
  };

  const handleClose = () => {
    formRef.current?.resetForm();
    onClose();
  };

  const getSubmitButtonText = () => {
    if (isSubmitting) return m.coInvestments_declineDeal_submittingButton();

    if (declineStage === "post-allocation")
      return m.coInvestments_declineAllocation_title();

    return m.coInvestments_declineDeal_title();
  };

  return (
    <ActionaryModal
      ModalButtonsGroupProps={{
        mainAction: () => formRef.current?.submitForm(),
        mainActionText: getSubmitButtonText(),
        mainActionVariant: "destructive",
        disabled: isSubmitting,
        secondaryAction: handleClose,
        secondaryActionText: m.btn_cancel(),
        disabledSecondary: isSubmitting,
      }}
      dataTestId="reject-deal-modal"
      fluidHeight
      open={isOpen}
      setOpen={handleClose}
      title={
        declineStage === "post-allocation"
          ? m.coInvestments_declineAllocation_title()
          : m.coInvestments_declineDeal_title()
      }
    >
      <Formik
        initialValues={{ reason: "" }}
        innerRef={formRef}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
      >
        <Form className="space-y-4 w-60 sm:w-96">
          <ThemedDropdownField
            isClearable={false}
            label={m.coInvestments_declineDeal_reasonLabel()}
            name="reason"
            options={DECLINE_REASONS[declineStage]}
            placeholder={m.coInvestments_declineDeal_reasonPlaceholder()}
          />
          <TextareaField
            isOptional
            label={m.coInvestments_declineDeal_commentLabel()}
            name="comment"
          />
        </Form>
      </Formik>
    </ActionaryModal>
  );
};
